import { useRef, useState } from "react";
import { useDbClient } from "@tanstack/react-db";
import { postDetailCollections } from "./post.collections";
import { createPostComment } from "./comment.mutations";
import { writeError } from "./forum-presentation";

export function useForumComments({
  postId,
  elevatedPrivileges,
}: {
  postId: string;
  elevatedPrivileges: boolean;
}) {
  const db = useDbClient();
  const comments = db.collection(
    postDetailCollections({ postId, elevatedPrivileges }).comments,
  );
  const [busyParentId, setBusyParentId] = useState<string | null | undefined>(
    undefined,
  );
  const [commentError, setCommentError] = useState<
    { parentId: string | null; message: string } | undefined
  >();
  const keys = useRef(
    new Map<string | null, { fingerprint: string; key: string }>(),
  );

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<{
    id: string;
    message: string;
  } | null>(null);
  function deleteComment(id: string): Promise<boolean> {
    setDeletingId(id);
    setDeleteError(null);
    return comments
      .delete(id)
      .when("settled")
      .then(() => true)
      .catch((reason: unknown) => {
        setDeleteError({
          id,
          message:
            reason instanceof Error
              ? reason.message
              : "Couldn't delete this comment.",
        });
        return false;
      })
      .finally(() => {
        setDeletingId(null);
      });
  }

  function submitComment({
    parentId,
    body,
  }: {
    parentId: string | null;
    body: string;
  }): Promise<boolean> {
    if (!body.trim()) return Promise.resolve(false);

    const existing = keys.current.get(parentId);
    const action =
      existing?.fingerprint === body
        ? existing
        : { fingerprint: body, key: crypto.randomUUID() };
    keys.current.set(parentId, action);

    setBusyParentId(parentId);
    setCommentError(undefined);
    return createPostComment({
      db,
      postId,
      parentId,
      body,
      idempotencyKey: action.key,
      elevatedPrivileges,
    })
      .then(() => {
        keys.current.delete(parentId);
        return true;
      })
      .catch((reason: unknown) => {
        setCommentError({
          parentId,
          message: writeError({ reason, fallback: "Comment failed." }),
        });
        return false;
      })
      .finally(() => setBusyParentId(undefined));
  }
  return {
    busyParentId,
    commentError,
    deletingId,
    deleteError,
    submitComment,
    deleteComment,
  };
}
